import React, { useEffect, useState } from 'react';
import AuditLogTable from '../../components/AuditLogTable';
import { API_BASE } from './../../config';
import { useAuth } from './../../context/AuthContext';
import './AuditLogsTab.css';

const PAGE_SIZE = 10;

// ---- date helpers ----
const toDateTimeLocal = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes())
  );
};

const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 7);
  return {
    from: toDateTimeLocal(from),
    to: toDateTimeLocal(to),
  };
};

const { from: DEFAULT_FROM, to: DEFAULT_TO } = getDefaultDateRange();

const AuditLogsTab = ({ tenantId: initialTenantId }) => {
  const { token } = useAuth();

  // table data
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // UI filter inputs
  const [tenantIdInput, setTenantIdInput] = useState(initialTenantId || '');
  const [actionTypeInput, setActionTypeInput] = useState('');
  const [fromInput, setFromInput] = useState(DEFAULT_FROM);
  const [toInput, setToInput] = useState(DEFAULT_TO);

  // applied filters (API-facing)
  const [filters, setFilters] = useState({
    tenantId: initialTenantId || '',
    actionType: '',
    from: DEFAULT_FROM,
    to: DEFAULT_TO,
  });

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams({
        page,
        size: PAGE_SIZE,
      });

      if (filters.tenantId) params.append('tenantId', filters.tenantId);
      if (filters.actionType) params.append('actionType', filters.actionType);
      if (filters.from) params.append('from', new Date(filters.from).toISOString());
      if (filters.to) params.append('to', new Date(filters.to).toISOString());

      const res = await fetch(
        `${API_BASE}/admin/audit-log?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      setLogs(data.content || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch audit logs', err);
      setLogs([]);
    }
  };

  // fetch only when applied filters or page change
  useEffect(() => {
    if (token) fetchLogs();
  }, [token, page, filters]);

  const applyFilters = () => {
    setPage(0);
    setFilters({
      tenantId: tenantIdInput,
      actionType: actionTypeInput,
      from: fromInput,
      to: toInput,
    });
  };

  const exportLogs = async (format) => {
    const params = new URLSearchParams();

    if (filters.tenantId) params.append('tenantId', filters.tenantId);
    if (filters.actionType) params.append('actionType', filters.actionType);
    if (filters.from) params.append('from', new Date(filters.from).toISOString());
    if (filters.to) params.append('to', new Date(filters.to).toISOString());
    params.append('format', format);

    try {
      const res = await fetch(
        `${API_BASE}/admin/audit-log/export?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) {
        throw new Error(`Export failed: ${res.statusText}`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${Date.now()}.${format === 'json' ? 'json' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error', err);
      alert('Failed to export audit logs: ' + err.message);
    }
  };

  return (
    <section className="audit-logs-tab">
      <div className="page-header">
        <h2 className="heading-lg">Audit Logs</h2>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <input
          placeholder="Tenant ID"
          value={tenantIdInput}
          onChange={(e) => setTenantIdInput(e.target.value)}
        />

        <select
          value={actionTypeInput}
          onChange={(e) => setActionTypeInput(e.target.value)}
        >
          <option value="">All Actions</option>
          <option value="TOKENIZE">TOKENIZE</option>
          <option value="DETOKENIZE">DETOKENIZE</option>
        </select>

        <input
          type="datetime-local"
          value={fromInput}
          onChange={(e) => setFromInput(e.target.value)}
        />

        <input
          type="datetime-local"
          value={toInput}
          onChange={(e) => setToInput(e.target.value)}
        />

        <button onClick={applyFilters}>Apply</button>

        <button onClick={() => exportLogs('xls')}>Export XLS</button>
        <button onClick={() => exportLogs('pdf')}>Export PDF</button>
      </div>

      <AuditLogTable logs={logs} />

      {/* Pagination */}
      <div className="pagination">
        <button
          disabled={page === 0}
          onClick={() => setPage((p) => p - 1)}
        >
          Prev
        </button>

        <span>
          {page + 1} / {totalPages}
        </span>

        <button
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>
    </section>
  );
};

export default AuditLogsTab;
